import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Card,
  Space,
  List,
  Tag,
  Tabs,
} from 'antd';
import {
  StarOutlined,
  StarFilled,
  DeleteOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import qs from 'qs';
import { API } from '../services/api';
import { IRSchedule } from '../types';
import { useIsMobile } from '../hooks/useIsMobile';
import CalendarView from './CalendarView';
import TableView from './TableView';

// Set Korean locale for dayjs
dayjs.locale('ko');

const styles = `
  .scrollable-grid::-webkit-scrollbar {
    width: 8px;
  }
  .scrollable-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .scrollable-grid::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  .scrollable-grid::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const fetchIRSchedules = async ({
  startDate,
  endDate,
  company,
}: {
  startDate?: string;
  endDate?: string;
  company?: string;
}) => {
  try {
    const paramsRaw = {
      startDate,
      endDate,
      company,
    };

    // Filter out undefined values
    const params = qs.stringify(
      Object.fromEntries(
        Object.entries(paramsRaw).filter(([, value]) => value)
      )
    );
    const response = await API.get(
      '/ir-schedules' + (params ? `?${params}` : '')
    );
    return response.data;
  } catch (error) {
    throw new Error('Network response was not ok');
  }
};

const TotalIR: React.FC = () => {
  const isMobile = useIsMobile();
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(dayjs());
  const [activeView, setActiveView] = useState<'calendar' | 'table'>(
    'calendar'
  );
  const [watchedCompanies, setWatchedCompanies] = useState<string[]>([]);
  const [isTodaySchedulesExpanded, setIsTodaySchedulesExpanded] =
    useState(true);

  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Load watched companies from localStorage on component mount
  useEffect(() => {
    const savedCompanies = localStorage.getItem('watchedCompanies');
    console.log('savedCompanies', savedCompanies);
    if (savedCompanies) {
      setWatchedCompanies(JSON.parse(savedCompanies));
    }
  }, []);

  //   // Save watched companies to localStorage when they change
  //   useEffect(() => {
  //     localStorage.setItem('watchedCompanies', JSON.stringify(watchedCompanies));
  //   }, [watchedCompanies]);

  const toggleWatchCompany = (company: string) => {
    const updatedCompanies = watchedCompanies.includes(company)
      ? watchedCompanies.filter((c) => c !== company)
      : [...watchedCompanies, company];

    // Save to localStorage first
    localStorage.setItem('watchedCompanies', JSON.stringify(updatedCompanies));

    // Then update state
    setWatchedCompanies(updatedCompanies);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      'irSchedules',
      {
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        company: companyFilter,
      },
    ],
    queryFn: () =>
      fetchIRSchedules({
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
        company: companyFilter,
      }),
    refetchOnWindowFocus: false,
  });

  const irSchedules: IRSchedule[] = data || [];

  const handleCompanySearch = (value: string) => {
    setCompanyFilter(value);
  };

  const handleDateRangeChange = (
    dates: [dayjs.Dayjs, dayjs.Dayjs] | null
  ) => {
    if (dates) {
      setDateRange(dates);
    }
  };

  const handlePanelChange = (date: dayjs.Dayjs) => {
    setDateRange([date.startOf('month'), date.endOf('month')]);
  };

  return (
    <div style={{ flex: 1 }}>
      {/* Today's IR Schedules */}
      {irSchedules.filter(
        (schedule: IRSchedule) => schedule.date === dayjs().format('YYYY-MM-DD')
      ).length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
            }}
          >
            <Typography.Title level={5} style={{ margin: 0 }}>
              오늘의 실적 발표 일정
            </Typography.Title>
            <Button
              type="text"
              onClick={() =>
                setIsTodaySchedulesExpanded(!isTodaySchedulesExpanded)
              }
            >
              {isTodaySchedulesExpanded ? '접기' : '펼치기'}
            </Button>
          </div>
          {isTodaySchedulesExpanded && (
            <div
              className="scrollable-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '12px',
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '8px',
              }}
            >
              {irSchedules
                .filter(
                  (schedule: IRSchedule) =>
                    schedule.date === dayjs().format('YYYY-MM-DD')
                )
                .map((schedule: IRSchedule) => (
                  <Card
                    key={`${schedule.company}-${schedule.time}`}
                    title={
                      <Space>
                        <Typography.Text strong>
                          {schedule.company}
                        </Typography.Text>
                        <Button
                          type="text"
                          icon={
                            watchedCompanies.includes(schedule.company) ? (
                              <StarFilled style={{ color: '#ffd700' }} />
                            ) : (
                              <StarOutlined />
                            )
                          }
                          onClick={() => toggleWatchCompany(schedule.company)}
                        />
                      </Space>
                    }
                    style={{ height: '100%' }}
                    bodyStyle={{ padding: '12px' }}
                    headStyle={{ padding: '8px 12px' }}
                  >
                    <div style={{ width: '100%' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <a
                          href={schedule.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {schedule.title}
                        </a>
                      </div>
                      <Space>
                        <Tag color="blue">{schedule.type}</Tag>
                        <Typography.Text type="secondary">
                          {schedule.time}
                        </Typography.Text>
                      </Space>
                    </div>
                  </Card>
                ))}
            </div>
          )}
        </div>
      )}

      {watchedCompanies.length > 0 &&
        watchedCompanies.some((company) =>
          irSchedules.some(
            (schedule: IRSchedule) => schedule.company === company
          )
        ) && (
          <div style={{ marginBottom: '20px' }}>
            <Typography.Title level={5}>관심 기업 실적 일정</Typography.Title>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '16px',
              }}
            >
              {watchedCompanies.map((company) => {
                const companySchedules = irSchedules.filter(
                  (schedule: IRSchedule) => schedule.company === company
                );
                if (companySchedules.length === 0) return null;

                return (
                  <Card
                    key={company}
                    title={
                      <Space>
                        <Typography.Text strong>{company}</Typography.Text>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => toggleWatchCompany(company)}
                        />
                      </Space>
                    }
                    style={{ height: '100%' }}
                  >
                    <List
                      dataSource={companySchedules}
                      renderItem={(schedule: IRSchedule) => (
                        <List.Item>
                          <div style={{ width: '100%' }}>
                            <div style={{ marginBottom: '8px' }}>
                              <a
                                href={schedule.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {schedule.title}
                              </a>
                            </div>
                            <Space>
                              <Tag color="blue">{schedule.type}</Tag>
                              <Typography.Text type="secondary">
                                {schedule.date} {schedule.time}
                              </Typography.Text>
                            </Space>
                          </div>
                        </List.Item>
                      )}
                    />
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Typography.Title
          level={5}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 0,
          }}
        >
          국내 기업 실적 발표 일정
        </Typography.Title>
      </div>

      <Tabs
        activeKey={activeView}
        onChange={(key) => setActiveView(key as 'calendar' | 'table')}
        items={[
          {
            key: 'calendar',
            label: '캘린더',
            children: (
              <CalendarView
                irSchedules={irSchedules}
                selectedDate={selectedDate}
                watchedCompanies={watchedCompanies}
                isMobile={isMobile}
                onDateChange={setSelectedDate}
                onPanelChange={handlePanelChange}
                onSelectSchedules={() => {}}
                onToggleWatch={toggleWatchCompany}
              />
            ),
          },
          {
            key: 'table',
            label: '목록',
            children: (
              <TableView
                irSchedules={irSchedules}
                isLoading={isLoading}
                isMobile={isMobile}
                dateRange={dateRange}
                watchedCompanies={watchedCompanies}
                onDateRangeChange={handleDateRangeChange}
                onCompanySearch={handleCompanySearch}
                onToggleWatch={toggleWatchCompany}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

export default TotalIR;
